import axios from 'axios';
import type { BusinessUI } from '../ui/business/types';
import type { PostUI } from '../ui/posts/types';
const envPeriod = process.env.REACT_APP_ANALYTICS_REQUESTS_PERIOD;
const errorExtendTime = process.env.REACT_APP_ANALYTICS_ERROR_EXTEND_TIME;
const envBulkSize = process.env.REACT_APP_ANALYTICS_BULK_SIZE;
const envMaxEventHistorySize = process.env.REACT_APP_ANALYTICS_MAX_EVENT_HISTORY_SIZE;
const DEFAULT_PERIOD = envPeriod ? parseInt(envPeriod, 10) : 2000;
const DEFAULT_BULK_SIZE = envBulkSize ? parseInt(envBulkSize, 10) : 100;
const MAX_EVENT_HISTORY_SIZE = envMaxEventHistorySize ? parseInt(envMaxEventHistorySize, 10) : 1000;
const ERROR_EXTEND_TIME = errorExtendTime ? parseInt(errorExtendTime, 10) : 2000;

export enum EventSource {
    FEED = 'feed',
    BUSINESS = 'business',
    WIDGET = 'widget',
    SUBSCRIPTIONS = 'subscriptions',
    SEARCH = 'search',
    POST = 'post',
    COLLECTION = 'collection',
}

export interface AnalyticEvent {
    object_id?: string;
    content_id?: string;
    clickout_target?: string;
    event_type: number;
    time?: string;
    event_source: EventSource;
    post_number?: number;
    slider_content_number?: number;
    not_by_user?: boolean;
    dj_request_id?: string;
}

export enum BusinessTransitionType {
    IN = 1004,
    OUT = 1005,
}

export enum VideoStates {
    VIDEO_STARTED = 12,
    VIDEO_FINISHED = 13,
    VIDEO_MUTED = 16,
    VIDEO_UN_MUTED = 17,
    VIDEO_PAUSED = 18,
    VIDEO_RESUMED = 19,
}

export interface VideoStateEventParams {
    eventType: VideoStates;
    wasNotTriggeredByUser?: boolean;
}

class AnalyticsService {
    private events: AnalyticEvent[] = [];
    private bulkSize: number = DEFAULT_BULK_SIZE;
    private intervalPeriod = DEFAULT_PERIOD;

    constructor(maxBulkSize: number = DEFAULT_BULK_SIZE) {
        this.bulkSize = maxBulkSize;
        this.sendBulk();
    }
    private pushEvent = (event: AnalyticEvent) => {
        this.events.push({ ...event, time: new Date().toISOString() });
        if (this.events.length > MAX_EVENT_HISTORY_SIZE) {
            this.events.shift();
        }
    };

    public trackClickOut(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: 9,
            clickout_target: 'button',
            event_source: params.eventSource,
            dj_request_id: params.djRequestId,
        });
    }

    public trackShare(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: 8,
            event_source: params.eventSource,
            dj_request_id: params.djRequestId,
        });
    }

    public trackSlideMoved(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        sliderContentNumber: number;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: 7,
            event_source: params.eventSource,
            slider_content_number: params.sliderContentNumber,
            dj_request_id: params.djRequestId,
        });
    }

    public trackPostExpanded(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        sliderContentNumber: number;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: 20,
            event_source: params.eventSource,
            slider_content_number: params.sliderContentNumber,
            dj_request_id: params.djRequestId,
        });
    }

    public trackVideoStateChange(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        state: VideoStateEventParams;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: params.state.eventType,
            not_by_user: params.state.wasNotTriggeredByUser,
            event_source: params.eventSource,
            dj_request_id: params.djRequestId,
        });
    }

    public trackPostVisible(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        isInViewport: boolean;
        postNumber: number;
        djRequestId?: string;
    }) {
        this.pushEvent({
            object_id: params.postId,
            content_id: params.mediaId,
            event_type: params.isInViewport ? 5 : 6,
            event_source: params.eventSource,
            post_number: params.postNumber,
            dj_request_id: params.djRequestId,
        });
    }

    public trackBusinessTransition(params: {
        businessId: BusinessUI['id'];
        eventSource: EventSource;
        businessTransitionType: BusinessTransitionType;
    }) {
        this.pushEvent({
            object_id: params.businessId,
            event_source: params.eventSource,
            event_type: params.businessTransitionType,
        });
    }

    private sendBulk = async () => {
        let lastBulk: AnalyticEvent[] = [];

        try {
            lastBulk = this.events.splice(Math.max(this.events.length - 1 - this.bulkSize, 0));
            if (lastBulk.length) {
                await axios.post('/user/actions', {
                    events: lastBulk,
                });
                this.intervalPeriod = DEFAULT_PERIOD;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 400) {
                if (lastBulk.length) {
                    this.events.push(...lastBulk);
                }

                this.intervalPeriod += ERROR_EXTEND_TIME;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        }
    };
}

export const analyticsService = new AnalyticsService(DEFAULT_BULK_SIZE);
